/*****************************************************************
 *  CUSTOMER BUTLER - THE SMALL SHARED PIECES                      *
 *  File: apps/web/src/pages/customer/customerParts.js             *
 *  What it does: the pieces every clean customer page uses:       *
 *    PageFrame   the page wrapper (look + module menu + footer)   *
 *    ModuleNav   Customers / Properties / CRM Setup               *
 *    LookFooter  the switch to the HR look or the classic look    *
 *    QuietLine   a plain message (empty, loading, not connected)  *
 *    Notice      a one-line notice with a meaning icon            *
 *    StatusChip  THE chip: icon + word + meaning colour           *
 *    Sheet       a bottom sheet / dialog for small forms          *
 *  Reads: nothing.  Writes: nothing.                              *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE PAGE FRAME AND THE MODULE MENU                 *
 *  SECTION 2 - MESSAGES AND THE CHIP                              *
 *  SECTION 3 - THE SHEET                                          *
 *****************************************************************/

import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from './customerIcons';
import { useButlerLook, SKIN_CLASSIC, SKIN_HR } from './customerLook';
import { useCustomerAccessView } from './customerAccessView';
import './customer.css';

/*****************************************************************
 *  SECTION 1 - THE PAGE FRAME AND THE MODULE MENU                 *
 *****************************************************************/

// WHY (2026-10-14, James): "Add a CRM Setup link inside the module's own
// nav." Agents, Pipeline, Recurring and Tasks stay hidden on purpose.
// WHY (2026-10-14, Honest UI, James via Fable): CRM Setup is offered only to
// a Customer Butler manager; the server refuses setup changes to everyone else.
const MODULE_LINKS = [
  { to: '/customers', label: 'Customers', managersOnly: false },
  { to: '/properties', label: 'Properties', managersOnly: false },
  { to: '/crm-setup', label: 'CRM Setup', managersOnly: true },
];

/* ---- ModuleNav() -- the module's own menu row ------------------
   Shows only the links this person's Customer Butler level allows. */
export function ModuleNav() {
  const accessView = useCustomerAccessView(); // what this person may be shown

  const shownLinks = MODULE_LINKS.filter((oneLink) => !oneLink.managersOnly || accessView.mayChangeSetup);

  return (
    <nav className="cb-nav" aria-label="Customer Butler">
      <span className="cb-nav-name">
        <Icon name="people" />
        Customer Butler
      </span>
      {shownLinks.map((oneLink) => (
        <NavLink
          key={oneLink.to}
          to={oneLink.to}
          className={({ isActive }) => (isActive ? 'cb-nav-link is-current' : 'cb-nav-link')}
        >
          {oneLink.label}
        </NavLink>
      ))}
    </nav>
  );
}

/* ---- LookFooter({ onChooseSkin }) ------------------------------
   The one-line switch to the other customer looks. Skins are only
   ever added: every earlier look is kept, one tap away.
   WHY (2026-10-15, James): the HR look is now the default; this
   clean look offers the way back to it beside the classic look. */
export function LookFooter({ onChooseSkin }) {
  if (!onChooseSkin) {
    return null;
  }

  return (
    <p className="cb-look">
      <button type="button" className="cb-link-btn" onClick={() => onChooseSkin(SKIN_HR)}>
        Switch to the HR Butler look
      </button>
      <span aria-hidden="true"> · </span>
      <button type="button" className="cb-link-btn" onClick={() => onChooseSkin(SKIN_CLASSIC)}>
        Switch to the classic look
      </button>
    </p>
  );
}

/* ---- PageFrame({ children, onChooseSkin }) ---------------------
   Links the design standard, draws the menu, the page, the footer. */
export function PageFrame({ children, onChooseSkin }) {
  useButlerLook();

  return (
    <div className="cb-page bc-page">
      <ModuleNav />
      {children}
      <LookFooter onChooseSkin={onChooseSkin} />
    </div>
  );
}

/*****************************************************************
 *  SECTION 2 - MESSAGES AND THE CHIP                              *
 *****************************************************************/

/* ---- QuietLine({ icon, children }) -- plain words, no box ---- */
export function QuietLine({ icon, children }) {
  return (
    <p className="cb-quiet">
      {icon ? <Icon name={icon} /> : null}
      <span>{children}</span>
    </p>
  );
}

/* ---- Notice({ tone, icon, children }) --------------------------
   tone: 'ok' | 'warn' | 'bad'. The icon carries the colour; the
   words always say the meaning too.                             */
export function Notice({ tone, icon, children }) {
  const toneClass = tone ? `cb-notice is-${tone}` : 'cb-notice';

  return (
    <p className={toneClass} role="status">
      <Icon name={icon || 'check'} />
      <span>{children}</span>
    </p>
  );
}

// Which meaning colour and icon each chip tone wears.
const CHIP_LOOKS = {
  ok: { className: 'bc-chip is-ok', icon: 'check' },
  warn: { className: 'bc-chip is-warn', icon: 'clock' },
  bad: { className: 'bc-chip is-bad', icon: 'warn' },
  neutral: { className: 'bc-chip is-neutral', icon: 'archive' },
  brand: { className: 'bc-chip is-brand', icon: 'link' },
};

/* ---- StatusChip({ tone, icon, word }) --------------------------
   The standard's chip: an icon AND a word AND the meaning colour
   (colour is never the only signal). One per row at most.       */
export function StatusChip({ tone, icon, word }) {
  const chipLook = CHIP_LOOKS[tone] || CHIP_LOOKS.neutral;

  return (
    <span className={chipLook.className}>
      <span className="bc-chip-i"><Icon name={icon || chipLook.icon} /></span>
      {word}
    </span>
  );
}

/*****************************************************************
 *  SECTION 3 - THE SHEET                                          *
 *****************************************************************/

/* ---- Sheet({ title, onClose, children }) -----------------------
   A bottom sheet on a phone, a dialog on a wide screen. Closes on
   Escape or a tap on the dimmed backdrop. Focus moves into it.  */
export function Sheet({ title, onClose, children }) {
  const sheetRef = useRef(null);

  useEffect(() => {
    const sheetBox = sheetRef.current;

    if (sheetBox) {
      sheetBox.focus();
    }

    function closeOnEscape(keyEvent) {
      if (keyEvent.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  function closeOnBackdrop(clickEvent) {
    const clickedTheBackdrop = clickEvent.target === clickEvent.currentTarget;

    if (clickedTheBackdrop) {
      onClose();
    }
  }

  return (
    <div className="cb-scrim" onMouseDown={closeOnBackdrop}>
      <div className="cb-sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={sheetRef}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}
